import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button, Form, InputGroup, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faTrash, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { useSelector } from 'react-redux';
import envConfig from '../../envConfig';
import dashboardApi from './services/dashboardApi';
import './PublicDashboardLink.css';
import { selectSessionInformation } from '../../redux/slices/sessionSlice';

const buildPublicUrl = (token) => {
  if (!token) return '';
  const base = (envConfig.raaUrl || '').replace(/\/$/, '');
  return `${base}/p/dashboard/${token}`;
};

const PublicDashboardLink = ({ dashboardId, publicToken, onTokenChange }) => {
  const { activeSubscription } = useSelector(selectSessionInformation);
  const hasPublicLinkAccess = Boolean(
    activeSubscription && activeSubscription.activeTierHasPublicUrl,
  );
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const url = useMemo(() => buildPublicUrl(publicToken), [publicToken]);
  const isEnabled = Boolean(publicToken);

  const handleToggle = useCallback(async () => {
    if (!dashboardId || busy) return;
    setBusy(true);
    try {
      if (isEnabled) {
        await dashboardApi.dashboards.deletePublicLink(dashboardId);
        onTokenChange(null);
      } else {
        const newToken = await dashboardApi.dashboards.regeneratePublicLink(dashboardId);
        onTokenChange(newToken);
      }
    } finally {
      setBusy(false);
    }
  }, [busy, dashboardId, isEnabled, onTokenChange]);

  const handleRegenerate = useCallback(async () => {
    if (!dashboardId || busy) return;
    setBusy(true);
    try {
      const newToken = await dashboardApi.dashboards.regeneratePublicLink(dashboardId);
      onTokenChange(newToken);
    } finally {
      setBusy(false);
    }
  }, [busy, dashboardId, onTokenChange]);

  const handleCopy = useCallback(async () => {
    if (!url) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else if (inputRef.current) {
        inputRef.current.select();
        document.execCommand('copy');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Ignore — user can still copy manually from the input.
    }
  }, [url]);

  // Feature-gated: only render for subscriptions whose active tier includes
  // the public URL capability. Guards against undefined / missing subscription.
  if (!hasPublicLinkAccess) {
    return null;
  }
  return (
    <div className="public-dashboard-link rounded border bg-white">
      <div className="d-flex align-items-center">
        <FontAwesomeIcon icon={faUsers} className="text-muted mr-2" />
        <span className="text-muted mr-2 text-nowrap">Public Dashboard Link</span>

        {!isEnabled ? (
          <Form.Check
            type="switch"
            id="public-dashboard-link-toggle"
            label="Enable"
            checked={false}
            disabled={busy || !dashboardId}
            onChange={handleToggle}
            className="mb-0"
          />
        ) : (
          <InputGroup size="sm" className="public-dashboard-link__group mb-0">
            <OverlayTrigger
              placement="top"
              overlay={(
                <Tooltip id="public-dashboard-link-copy-tooltip">
                  {copied ? 'Copied!' : 'Click to copy'}
                </Tooltip>
              )}
            >
              <Form.Control
                ref={inputRef}
                type="text"
                value={url}
                readOnly
                onClick={handleCopy}
                className="public-dashboard-link__input"
              />
            </OverlayTrigger>
            <InputGroup.Append>
              <Button
                variant="outline-secondary"
                onClick={handleRegenerate}
                disabled={busy}
                title="Regenerate link"
              >
                <FontAwesomeIcon icon={faArrowsRotate} spin={busy} />
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleToggle}
                disabled={busy}
                title="Disable public link"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </InputGroup.Append>
          </InputGroup>
        )}
      </div>
    </div>
  );
};

PublicDashboardLink.propTypes = {
  dashboardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  publicToken: PropTypes.string,
  onTokenChange: PropTypes.func.isRequired,
};

PublicDashboardLink.defaultProps = {
  dashboardId: null,
  publicToken: null,
};

export default PublicDashboardLink;
